// The Increase box on the Record (S14, part 2 of the increase review; Record board, Oct 30): the
// words, and who gets which buttons. Phase 1 (Yehuda, Oct 30): any AE answers; the Manager or the
// Admin marks Looked over only (to reshop she uses Reassign); the Renewal VA sees the box only.
import { isIncrease, type IncreaseRule } from '@safeside/shared';
import type { RenewalRow } from '../auth/api';
import type { Role } from '../roles';
import { stampTime } from './fetch-lines';
import { stageOf } from './renewal-lines';

export type IncreaseAnswer = 'looked_over' | 'reshop';

const gone = 'someone no longer in the portal';

/**
 * The box's title and line, or null when there is no box: below the rule (also after a premium
 * correction, the answer kept), or a closed renewal nobody answered. `premium` is the renewal
 * premium the Record shows (the dec page's, read or corrected, otherwise AMS360's).
 */
export function increaseBox(
  r: RenewalRow,
  rule: IncreaseRule,
  premium: number | null,
): { title: string; line: string } | null {
  if (!isIncrease(rule, r.priorPremium, premium)) return null;
  const review = r.increaseReview;
  const who = review.reviewedBy?.name ?? gone;
  const at = review.reviewedAt ? stampTime(review.reviewedAt) : '';
  if (review.state === 'looked_over') {
    return { title: 'Increase looked over', line: `By ${who}, ${at}. No reshop.` };
  }
  if (review.state === 'reshop') {
    return {
      title: 'Reshopping',
      line: `${who} reshops it, since ${at}. The AE contacts the insured outside the portal.`,
    };
  }
  if (stageOf(r) === 'closed') return null;
  return {
    title: 'Increase to look over',
    line: 'Waiting for an AE to look it over. The VA sends the email as usual.',
  };
}

/** The buttons, left to right, only while nobody answered and the renewal is open. */
export function increaseButtons(r: RenewalRow, role: Role): IncreaseAnswer[] {
  if (r.increaseReview.state !== 'none' || stageOf(r) === 'closed') return [];
  if (role === 'Account Executive') return ['reshop', 'looked_over'];
  if (role === 'Manager' || role === 'Admin') return ['looked_over'];
  return [];
}

export const answerLabels: Record<IncreaseAnswer, string> = {
  reshop: "I'll reshop it",
  looked_over: 'Looked over',
};

export const reshopConfirm = (insured: string) => ({
  title: `Take over ${insured} to reshop?`,
  line: 'It moves to your queue with status Increase. Contact the insured outside the portal.',
  action: 'Take over',
});

export const answeredToast = (answer: IncreaseAnswer, insured: string) =>
  answer === 'reshop'
    ? `${insured} is with you to reshop. Status: Increase.`
    : 'Looked over. No reshop.';

/** What went wrong, in plain words, when the server refused the answer or its Undo. */
export function answerError(error: string | undefined, undo = false): string {
  if (undo) return 'Could not undo it. It changed since.';
  if (error === 'already_answered') return 'Someone already answered this increase.';
  if (error === 'not_an_increase') return 'This renewal is no longer at or over the increase rule.';
  if (error === 'renewal_closed') return 'This renewal is closed.';
  if (error === 'changed') return 'It changed since you opened it. Look again.';
  return 'Could not save the answer. Try again.';
}
